/**
 * 📝 REVIEWS & RATINGS SERVICE
 * Admin API service for managing reviews and ratings
 * Backend: https://polarride-api-17dr.vercel.app/api/admin/reviews
 */

import { getApiBaseUrl } from '@/config/environment';


// ========================================
// INTERFACES
// ========================================

export interface ReviewReport {
  id: string;
  reviewId: string;
  reporterId: string;
  reporterName?: string;
  reporterEmail?: string;
  reason: string;
  additionalInfo?: string;
  status: 'pending' | 'resolved' | 'dismissed';
  resolvedBy?: string;
  resolvedAt?: string;
  createdAt: string;
}

export interface Review {
  id: string;
  bookingId: string;
  bookingType: 'vehicle' | 'driver' | 'renter';
  reviewerId: string;
  reviewerName: string;
  reviewerEmail: string;
  reviewerAvatar?: string;
  reviewerType?: 'renter' | 'driver' | 'host';
  targetId: string;
  targetType: 'vehicle' | 'driver' | 'renter';
  targetName?: string;
  // For vehicle reviews
  vehicleId?: string;
  vehicleName?: string;
  hostId?: string;
  hostName?: string;
  // For driver reviews
  driverId?: string;
  driverName?: string;
  driverEmail?: string;
  // Rating and feedback
  rating: number;
  categories?: Record<string, number>;
  comment: string;
  // Engagement metrics
  helpfulCount: number;
  reportCount: number;
  reports?: ReviewReport[];
  // Status
  status: 'pending' | 'published' | 'active' | 'removed' | 'flagged';
  removalReason?: string;
  removedBy?: string;
  removedAt?: string;
  // Booking context
  bookingStartDate?: string;
  bookingEndDate?: string;
  bookingTotalCost?: number;
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export interface ReviewStatistics {
  totalReviews: number;
  activeReviews: number;
  removedReviews: number;
  flaggedReviews: number;
  averageRating: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  vehicleReviews: {
    total: number;
    average: number;
  };
  driverReviews: {
    total: number;
    average: number;
  };
  renterReviews: {
    total: number;
    average: number;
  };
  recentTrends: {
    last7Days: number;
    last30Days: number;
    growthRate: number;
  };
  pendingReports: number;
}

export interface ReviewFilters {
  page?: number;
  limit?: number;
  status?: 'all' | 'pending' | 'published' | 'active' | 'removed' | 'flagged';
  rating?: 1 | 2 | 3 | 4 | 5;
  bookingType?: 'vehicle' | 'driver' | 'renter';
  startDate?: string;
  endDate?: string;
  search?: string;
}

export interface ModerateReviewRequest {
  action: 'approve' | 'remove';
  reason?: string;
  adminUserId: string;
}

export interface ResolveReportRequest {
  action: 'resolve' | 'dismiss';
  notes?: string;
  adminUserId: string;
}

// ========================================
// SERVICE CLASS
// ========================================

class ReviewsService {
  private baseUrl = `${getApiBaseUrl()}/api/admin/reviews`;

  private getAuthHeaders() {
    return {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Helper to get admin user ID from localStorage
   */
  private getAdminUserId(): string {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.id || user.userId || 'admin-default';
      } catch (e) {
      }
    }
    return 'admin-default';
  }

  /**
   * 🎬 Initialize renter reviews database table
   * POST /api/admin/reviews/init-renter-reviews
   */
  async initRenterReviews(): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {

      const response = await fetch(`${this.baseUrl}/init-renter-reviews`, {
        method: 'POST',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Init Error ${response.status}:`, errorText);
        throw new Error(`Failed to initialize renter reviews table (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error initializing renter reviews:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initialize renter reviews table'
      };
    }
  }

  /**
   * 📋 Get all reviews with filters and pagination
   * GET /api/admin/reviews
   */
  async getReviews(filters: ReviewFilters = {}): Promise<{
    success: boolean;
    data: {
      reviews: Review[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasMore: boolean;
      };
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.rating) params.append('rating', String(filters.rating));
      if (filters.bookingType) params.append('bookingType', filters.bookingType);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.search) params.append('search', filters.search);

      const url = `${this.baseUrl}${params.toString() ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Reviews Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch reviews (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching reviews:', error);
      return {
        success: false,
        data: {
          reviews: [],
          pagination: {
            page: filters.page || 1,
            limit: filters.limit || 20,
            total: 0,
            totalPages: 0,
            hasMore: false
          }
        },
        error: error instanceof Error ? error.message : 'Failed to fetch reviews'
      };
    }
  }

  /**
   * 🔍 Get single review details with reports
   * GET /api/admin/reviews/:id
   */
  async getReviewById(reviewId: string): Promise<{
    success: boolean;
    data?: {
      review: Review;
    };
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/${reviewId}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Review Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch review (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching review:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch review'
      };
    }
  }

  /**
   * ✏️ Moderate review (approve or remove)
   * PATCH /api/admin/reviews/:id/moderate
   */
  async moderateReview(
    reviewId: string,
    action: 'approve' | 'remove',
    reason?: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      review: Review;
      moderatedBy: string;
      moderatedAt: string;
    };
    error?: string;
  }> {
    try {
      const adminUserId = this.getAdminUserId();
      const url = `${this.baseUrl}/${reviewId}/moderate`;

      const requestBody: ModerateReviewRequest = {
        action,
        adminUserId,
        ...(reason && { reason })
      };


      const response = await fetch(url, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Moderate Review Error ${response.status}:`, errorText);

        let errorMessage = `Failed to moderate review (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error moderating review:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to moderate review'
      };
    }
  }

  /**
   * 📊 Get review statistics
   * GET /api/admin/reviews/statistics
   */
  async getStatistics(startDate?: string, endDate?: string): Promise<{
    success: boolean;
    data?: ReviewStatistics;
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `${this.baseUrl}/statistics${params.toString() ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Statistics Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch statistics (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching statistics:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch statistics'
      };
    }
  }

  /**
   * 📋 Get all review reports
   * GET /api/admin/reviews/reports
   */
  async getReports(filters?: {
    status?: 'pending' | 'resolved' | 'dismissed';
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data?: {
      reports: ReviewReport[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasMore: boolean;
      };
    };
    error?: string;
  }> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));

      const url = `${this.baseUrl}/reports${params.toString() ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Get Reports Error ${response.status}:`, errorText);
        throw new Error(`Failed to fetch reports (${response.status})`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error fetching reports:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch reports'
      };
    }
  }

  /**
   * ✅ Resolve a review report
   * PATCH /api/admin/reviews/reports/:reportId/resolve
   */
  async resolveReport(
    reportId: string,
    action: 'resolve' | 'dismiss',
    notes?: string
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      report: ReviewReport;
      resolvedBy: string;
      resolvedAt: string;
    };
    error?: string;
  }> {
    try {
      const adminUserId = this.getAdminUserId();
      const url = `${this.baseUrl}/reports/${reportId}/resolve`;

      const requestBody: ResolveReportRequest = {
        action,
        adminUserId,
        ...(notes && { notes })
      };


      const response = await fetch(url, {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Resolve Report Error ${response.status}:`, errorText);

        let errorMessage = `Failed to resolve report (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        return {
          success: false,
          error: errorMessage
        };
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('❌ Error resolving report:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resolve report'
      };
    }
  }
}

export const reviewsService = new ReviewsService();
export default reviewsService;
